import { AzureFunctionTokenProvider } from "@bezoned/shared/fluid/azure-function-token-provider.js";
import { Office } from "@bezoned/shared/fluid/office-schema-factory.js";
import { AzureClient } from "@fluidframework/azure-client";
import type {
  AzureContainerServices,
  AzureRemoteConnectionConfig,
} from "@fluidframework/azure-client";
import {
  AttachState,
  SharedTree,
  TreeViewConfiguration,
} from "fluid-framework";
import type {
  IFluidContainer,
  ITree,
  SharedObjectKind,
  TreeView,
} from "fluid-framework";
import { status } from "http-status";
import { Duration } from "luxon";

import {
  recreateTeamFluidContainer,
  teamOfficeGet,
} from "../../services/api.service.js";
import { useTeamBridgeStore } from "../../store/team-bridge.store.js";
import { env } from "../../utils/env.js";
import { useAuthBridgeStore } from "../auth/auth-bridge.store.js";
import { createFluidTelemetryLogger } from "./fluid-telemetry.js";
import { FluidRecreationError, RedirectToLobbyError } from "./fluid.errors.js";

const RECREATE_RETRY_DELAY_MS = Duration.fromObject({ seconds: 1 }).as(
  "milliseconds"
);
const MAX_RECREATE_ATTEMPTS = 45;
const recreationPromises = new Map<string, Promise<string>>();

const delay = async (ms: number) =>
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Handle container incompatibility by recreating the container with the current schema.
 * Returns the new container ID on success, or throws an error.
 * @returns {Promise<string>} The new container ID
 * @throws {RedirectToLobbyError} If no team is selected
 * @throws {FluidRecreationError} If recreation fails
 */
const waitForRecreatedContainerId = async ({
  currentContainerId,
  msTeamId,
}: {
  currentContainerId: string;
  msTeamId: string;
}): Promise<string> => {
  for (let attempt = 0; attempt < MAX_RECREATE_ATTEMPTS; attempt += 1) {
    const office = await teamOfficeGet({ msTeamId });
    const nextContainerId = office.team.fluidContainerId;

    if (nextContainerId && nextContainerId !== currentContainerId) {
      return nextContainerId;
    }

    if (attempt < MAX_RECREATE_ATTEMPTS - 1) {
      await delay(RECREATE_RETRY_DELAY_MS);
    }
  }

  throw new FluidRecreationError(
    "Container recreation is still in progress. Please retry."
  );
};

const handleContainerIncompatibility = async (
  currentContainerId: string
): Promise<string> => {
  const { msTeamId } = useTeamBridgeStore.getState();

  if (!msTeamId) {
    throw new RedirectToLobbyError("Team not selected", "no-team");
  }

  const pendingRecreation = recreationPromises.get(msTeamId);
  if (pendingRecreation) {
    return pendingRecreation;
  }

  try {
    const recreationPromise = (async () => {
      const result = await recreateTeamFluidContainer({
        currentContainerId,
        msTeamId,
      });
      const nextContainerId = result.body?.container_id;

      if (
        result.status === status.OK &&
        nextContainerId &&
        nextContainerId !== currentContainerId
      ) {
        return nextContainerId;
      }

      if (
        result.status === status.OK &&
        nextContainerId === currentContainerId
      ) {
        throw new FluidRecreationError(
          "Container recreation returned the same incompatible container."
        );
      }

      if (result.status !== status.OK && result.status !== status.ACCEPTED) {
        throw new FluidRecreationError("Invalid response from API");
      }

      return waitForRecreatedContainerId({
        currentContainerId,
        msTeamId,
      });
    })();

    recreationPromises.set(msTeamId, recreationPromise);
    return await recreationPromise;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to recreate container";
    const options: { canRetry: boolean; originalError?: Error } = {
      canRetry: true,
    };
    if (error instanceof Error) {
      options.originalError = error;
    }
    throw new FluidRecreationError(errorMessage, options);
  } finally {
    recreationPromises.delete(msTeamId);
  }
};

export const getAzureClientForFluid = () => {
  const { getAccessToken } = useAuthBridgeStore.getState();

  const config = {
    endpoint: env.PUBLIC_FLUID_RELAY_ENDPOINT,
    tenantId: env.PUBLIC_FLUID_RELAY_TENANT,
    tokenProvider: new AzureFunctionTokenProvider({
      apiDomain: env.PUBLIC_API_URL,
      clientAuthTokenProvider: getAccessToken,
    }),
    type: "remote",
  } satisfies AzureRemoteConnectionConfig;

  // Create new client instance and store it
  return new AzureClient({
    connection: config,
    logger: createFluidTelemetryLogger(useTeamBridgeStore.getState().msTeamId),
  });
};

export type FluidSharedTree = Awaited<
  ReturnType<typeof joinFluidContainer>
>["sharedTree"];
export type FluidContainer = Awaited<
  ReturnType<typeof joinFluidContainer>
>["container"];
export type FluidContainerId = Awaited<
  ReturnType<typeof joinFluidContainer>
>["containerId"];
export type FluidServices = Awaited<
  ReturnType<typeof joinFluidContainer>
>["services"];

export const joinFluidContainer = async (
  containerId: string,
  attemptedContainerIds = new Set<string>()
): Promise<{
  container: IFluidContainer<{
    initialObjects: {
      officeTree: SharedObjectKind<ITree>;
    };
  }>;
  containerId: string;
  services: AzureContainerServices;
  sharedTree: TreeView<typeof Office>;
}> => {
  const client = await getAzureClientForFluid();

  if (!containerId) {
    throw new Error("No Fluid container id");
  }

  const treeConfiguration = new TreeViewConfiguration({ schema: Office });
  const containerSchema = { initialObjects: { officeTree: SharedTree } };

  const { container, services } = await client.getContainer(
    containerId,
    containerSchema,
    "2"
  );

  const sharedTree =
    container.initialObjects.officeTree.viewWith(treeConfiguration);

  if (sharedTree.compatibility.canUpgrade) {
    sharedTree.upgradeSchema();
  } else if (
    sharedTree.compatibility.canInitialize ||
    !sharedTree.compatibility.isEquivalent
  ) {
    if (attemptedContainerIds.has(containerId)) {
      try {
        await container.disconnect();
      } catch {
        // Ignore cleanup failures when abandoning an incompatible container.
      }
      container.dispose();

      throw new FluidRecreationError(
        "Container recreation did not produce a compatible office container."
      );
    }

    attemptedContainerIds.add(containerId);

    try {
      await container.disconnect();
    } catch {
      // Ignore cleanup failures when abandoning an incompatible container.
    }
    container.dispose();

    // Schema is incompatible - recreate container and reconnect with new ID
    const newContainerId = await handleContainerIncompatibility(containerId);
    return joinFluidContainer(newContainerId, attemptedContainerIds);
  }

  let resolvedContainerId = containerId;
  if (container.attachState === AttachState.Detached) {
    resolvedContainerId = await container.attach();
  }

  return { container, containerId: resolvedContainerId, services, sharedTree };
};
